import { Select, Tag } from 'antd';
import React from 'react';
import { PeopleInterface } from 'type/peopleType';

// Enhanced Cast Editor Component (editable)
interface EnhancedCastEditorProps {
  value?: { cast: number; type: string }[];
  onChange?: (value: { cast: number; type: string }[]) => void;
  peopleList?: PeopleInterface[];
  onSearchPeople?: (searchTerm: string) => void;
}

const EnhancedCastEditor: React.FC<EnhancedCastEditorProps> = ({
  value = [],
  onChange,
  peopleList = [],
  onSearchPeople
}) => {
  // Define cast types to use (only 3 from existing typeLabels)
  const castTypes = [
    { value: 'director', label: 'Director' },
    { value: 'writer', label: 'Writer' },
    { value: 'actor', label: 'Actor' }
  ];

  // Cast data grouped by type
  const groupedCast = castTypes.reduce((acc, typeOption) => {
    acc[typeOption.value] = value.filter(item => item.type === typeOption.value);
    return acc;
  }, {} as Record<string, { cast: number; type: string }[]>);

  // Convert cast ID to name
  const getCastName = (castId: number): string => {
    const person = peopleList.find(p => p.id === castId);
    return person ? person.name : `Cast ID: ${castId}`;
  };

  // Handler for cast changes of a specific type
  const handleCastChange = (type: string, selectedCastIds: number[]) => {
    // Keep casts of other types
    const otherTypeCasts = value.filter(item => item.type !== type);

    // Add newly selected casts as the specified type
    const newTypeCasts = selectedCastIds.map(castId => ({
      cast: castId,
      type: type
    }));

    // Update the entire enhanced_cast array
    const updatedValue = [...otherTypeCasts, ...newTypeCasts];
    onChange?.(updatedValue);
  };

  return (
    <div style={{ border: '1px solid #d9d9d9', borderRadius: '6px', padding: '12px' }}>
      {castTypes.map(typeOption => {
        const currentCasts = groupedCast[typeOption.value] || [];
        const currentCastIds = currentCasts.map(item => item.cast);

        return (
          <div key={typeOption.value} style={{ marginBottom: '16px' }}>
            <div style={{ marginBottom: '8px' }}>
              <Tag color="blue" style={{ fontSize: '12px', padding: '2px 8px' }}>
                {typeOption.label}
              </Tag>
            </div>
            <Select
              mode="multiple"
              showSearch
              style={{ width: '100%' }}
              placeholder={`Search and select ${typeOption.label}`}
              value={currentCastIds}
              onChange={(selectedIds: number[]) => handleCastChange(typeOption.value, selectedIds)}
              onSearch={onSearchPeople}
              filterOption={false}
              options={peopleList.map(person => ({
                label: person.i18n_name?.en || person.name,
                value: person.id,
              }))}
              tagRender={({ label, value, closable, onClose }) => {
                const person = peopleList.find(p => p.id === value);
                const displayName = person?.i18n_name?.en || person?.name || label;

                return (
                  <span
                    onMouseDown={e => {
                      // Stop event propagation only if not clicking the close button
                      if ((e.target as HTMLElement).closest('.ant-tag-close-icon')) {
                        return;
                      }
                      // Stop event propagation to prevent Select from opening on click for text selection
                      e.stopPropagation();
                    }}
                    onMouseUp={e => {
                      // Stop event propagation only if not clicking the close button
                      if ((e.target as HTMLElement).closest('.ant-tag-close-icon')) {
                        return;
                      }
                      // Prevent Select from grabbing focus after text selection completes
                      e.stopPropagation();
                      // Add a slight delay to allow text selection to complete
                      setTimeout(() => {
                        // Prevent Select from grabbing focus even after text selection is complete
                        const selection = window.getSelection();
                        if (selection && selection.toString().length > 0) {
                          // Prevent Select focus if text is selected
                          e.stopPropagation();
                        }
                      }, 0);
                    }}
                    onClick={e => {
                      // Stop event propagation only if not clicking the close button
                      if ((e.target as HTMLElement).closest('.ant-tag-close-icon')) {
                        return;
                      }
                      // Prevent Select from opening on click after text selection
                      e.stopPropagation();
                    }}
                    style={{ display: 'inline-block' }}
                  >
                    <Tag
                      closable={closable}
                      onClose={onClose}
                      style={{
                        userSelect: 'text',
                        WebkitUserSelect: 'text',
                        MozUserSelect: 'text',
                        msUserSelect: 'text',
                        cursor: 'text',
                      }}
                    >
                      {displayName}
                    </Tag>
                  </span>
                );
              }}
            />
          </div>
        );
      })}
    </div>
  );
};

export default EnhancedCastEditor;

